import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, UploadCloud, Loader2, Mic } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 20 * 1024 * 1024;

export default function VoiceCloneModal({ open, onClose, onCloned }) {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const canSubmit = name.trim() && file && consent && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (file.size > MAX_BYTES) {
      toast.error(t("toasts.fileTooLarge"), {
        description: t("toasts.fileTooLargeDesc"),
      });
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", name.trim());
      form.append("consent", "true");
      const { data } = await axios.post(`${API}/voices/clone`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      toast.success(t("clone.cloneOk"), {
        description: t("clone.cloneOkDesc", { name: data.name }),
      });
      onCloned?.(data);
      onClose();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("clone.cloneFailed");
      toast.error(t("clone.cloneFailed"), { description: detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="clone-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#EFF4FF] text-[#002FA7]">
              <Mic className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <h3 className="font-serif text-2xl text-zinc-950">
              {t("clone.modalTitle")}
            </h3>
          </div>
          <button
            data-testid="clone-modal-close"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="font-sans text-sm text-zinc-600 leading-relaxed mb-6">
          {t("clone.modalSubtitle")}
        </p>

        <div className="space-y-4">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              {t("clone.voiceName")}
            </label>
            <input
              data-testid="clone-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("clone.voiceNamePlaceholder")}
              maxLength={60}
              className="mt-2 w-full h-11 rounded-md border border-zinc-300 bg-white px-3 font-sans text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
            />
          </div>

          <div>
            <label className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
              {t("clone.audioFile")}
            </label>
            <label
              data-testid="clone-file-drop"
              className="mt-2 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-300 rounded-md py-6 cursor-pointer hover:border-[#002FA7]/50 hover:bg-[#EFF4FF] transition-colors duration-200"
            >
              <UploadCloud className="h-6 w-6 text-[#002FA7]" />
              <span className="font-sans text-sm text-zinc-800 font-medium">
                {file ? file.name : t("clone.audioFile")}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {t("clone.audioHint")}
              </span>
              <input
                data-testid="clone-file-input"
                type="file"
                accept="audio/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
              />
            </label>
          </div>

          <label
            data-testid="clone-consent"
            className="flex items-start gap-2 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-zinc-400 accent-[#002FA7]"
            />
            <span className="font-sans text-sm text-zinc-700 leading-snug">
              {t("clone.consent")}
            </span>
          </label>

          <p
            data-testid="clone-cost-hint"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-500"
          >
            {t("clone.costHint")}
          </p>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            data-testid="clone-cancel"
            onClick={onClose}
            className="rounded-md border border-zinc-200 bg-white px-4 py-2 font-sans text-sm font-medium text-zinc-800 hover:bg-zinc-50 transition-colors duration-200"
          >
            {t("clone.cancel")}
          </button>
          <button
            data-testid="clone-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-[#002FA7] px-4 py-2 font-sans text-sm font-semibold text-white hover:bg-[#00258A] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 inline-flex items-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("clone.submitting")}
              </>
            ) : (
              t("clone.submit")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
